<template>
  <main id="quote-detail-page" class="page-container">
    <!-- LOADER -->
    <div v-if="isLoading" class="detail-loader" aria-live="polite">
      <div class="spinner" role="status"></div>
      <p>Carico il preventivo…</p>
    </div>

    <!-- ERRORE -->
    <div v-else-if="loadError" class="detail-error">
      <p>{{ loadError }}</p>
      <button class="btn-secondary" @click="goBack">Torna ai preventivi</button>
    </div>

    <!-- CONTENUTO -->
    <template v-else>
      <header class="detail-header">
        <h2 class="detail-title">Dettaglio preventivo #{{ quoteId }}</h2>
        <span v-if="quote?.state" class="detail-state">{{ quote.state }}</span>
        <!-- sc-48744: dalla ricerca articoli si arriva anche su preventivi
             archiviati o eliminati: qui si vede subito. -->
        <span v-if="isEliminato" class="detail-state detail-state--deleted">
          Eliminato
        </span>
        <span
          v-else-if="isArchived"
          class="detail-state detail-state--archived"
        >
          Archiviato
        </span>
      </header>

      <p v-if="isEliminato" class="detail-banner detail-banner--deleted">
        Questo preventivo è stato eliminato dalle liste: non compare nei
        preventivi né nella ricerca articoli. Per rimetterlo nelle liste scrivi
        a Liberotratto indicando il numero.
      </p>
      <p v-else-if="isArchived" class="detail-banner">
        Questo preventivo è archiviato: lo trovi nella scheda «Archiviati», non
        tra i preventivi attivi.
      </p>

      <!-- DETTAGLIO CLIENTE + PREVENTIVO (2 colonne) -->
      <div class="detail-two-cols">
        <!-- DETTAGLIO CLIENTE -->
        <section class="detail-section">
          <h3 class="detail-section__title">Dettaglio cliente</h3>
          <div class="detail-grid">
            <div class="detail-row">
              <span class="detail-label">Cliente</span>
              <span class="detail-value">{{ client.clientName || "N/D" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Telefono</span>
              <span class="detail-value">{{
                client.phoneNumber || "N/D"
              }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Email</span>
              <span class="detail-value">{{ client.email || "N/D" }}</span>
            </div>
            <div class="detail-row" v-if="client.pec">
              <span class="detail-label">PEC</span>
              <span class="detail-value">{{ client.pec }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Partita IVA</span>
              <span class="detail-value">{{ client.vatNumber || "N/D" }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Codice fiscale</span>
              <span class="detail-value">{{ client.taxCode || "N/D" }}</span>
            </div>
          </div>
        </section>

        <!-- DETTAGLIO PREVENTIVO -->
        <section class="detail-section">
          <h3 class="detail-section__title">Dettaglio preventivo</h3>
          <div class="detail-grid">
            <div class="detail-row">
              <span class="detail-label">Data</span>
              <span class="detail-value">{{ formattedDate }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Indirizzo di consegna</span>
              <a
                v-if="quote?.location?.link"
                :href="quote.location.link"
                target="_blank"
                rel="noopener noreferrer"
                class="detail-value custom-link"
              >
                {{ quote.location.address || "Indirizzo non disponibile" }}
              </a>
              <span v-else class="detail-value">{{
                quote?.location?.address || "Indirizzo non disponibile"
              }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Metodo di pagamento</span>
              <span class="detail-value">{{
                quote?.payment_method || "N/D"
              }}</span>
            </div>
            <div
              class="detail-row detail-row--full"
              v-if="quote?.technical_queries"
            >
              <span class="detail-label">Note tecniche</span>
              <span class="detail-value">{{ quote.technical_queries }}</span>
            </div>
          </div>
        </section>
      </div>

      <hr />

      <!-- ARTICOLI -->
      <section class="detail-section">
        <h3 class="detail-section__title">Articoli</h3>
        <div class="detail-table-wrap">
          <table class="detail-items-table">
            <thead>
              <tr>
                <th class="col-left">Articolo</th>
                <th class="col-left col-secondary">Categoria</th>
                <th class="col-right">Quantità</th>
                <th class="col-center col-secondary">U.m.</th>
                <th class="col-right col-secondary">Venduti</th>
                <th class="col-right">Prezzo unitario</th>
                <th class="col-right col-secondary">IVA</th>
                <th v-if="isConfirmed" class="col-center">
                  <div class="delivered-th">
                    <span>Consegnato si/no</span>
                    <input
                      ref="masterCheckbox"
                      type="checkbox"
                      :checked="allDelivered"
                      :disabled="
                        isArchived ||
                        deliverableItems.length === 0 ||
                        togglingAll
                      "
                      @change="toggleAllDelivered($event.target.checked)"
                      :title="
                        isArchived
                          ? 'Preventivo archiviato: consegne in sola lettura'
                          : hasBlockedItems
                          ? 'Alcuni articoli sono da ordinare: reintegra il magazzino (ORDINA) per abilitarne la consegna'
                          : 'Segna tutti come consegnati'
                      "
                      aria-label="Segna tutti come consegnati"
                    />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, index) in items"
                :key="item.quoteItemId ?? index"
                class="row-clickable"
                @click="onRowClick($event, item)"
              >
                <td class="col-left detail-item-name">{{ item.name }}</td>
                <td class="col-left col-secondary">{{ item.category }}</td>
                <td class="col-right">{{ item.qty }}</td>
                <td class="col-center col-secondary">{{ item.unit }}</td>
                <td class="col-right col-secondary">
                  <span
                    v-if="showProcureBadge(item)"
                    class="venduti venduti--procure"
                    :title="procureTitle(item)"
                  >
                    {{ item.sold }}
                    <span class="badge-procure"
                      >da ordinare {{ item.quantityToProcure }}</span
                    >
                  </span>
                  <span v-else class="venduti">
                    {{ item.sold }}
                    <span
                      v-if="showCommittedBadge(item)"
                      class="badge-committed"
                      :title="committedTitle(item)"
                    >
                      impegnato
                    </span>
                  </span>
                </td>
                <td class="col-right">{{ formatPriceToPrint(item.price) }}</td>
                <td class="col-right col-secondary">{{ item.iva }}%</td>
                <td v-if="isConfirmed" class="col-center delivered-cell">
                  <!-- Archiviato: sola lettura, nessuna gestione consegna né ORDINA -->
                  <input
                    v-if="isArchived"
                    type="checkbox"
                    :checked="item.isDelivered"
                    disabled
                    title="Preventivo archiviato: consegne in sola lettura"
                    aria-label="Consegnato (sola lettura)"
                  />
                  <template v-else>
                    <!-- Riga di magazzino: checkbox / ORDINA (sc-47876) -->
                    <template v-if="item.isStock">
                      <input
                        v-if="cellState(item) === 'checkbox'"
                        type="checkbox"
                        v-model="item.isDelivered"
                        :disabled="togglingDelivery[index] || togglingAll"
                        @change="onToggleDelivered(item, index)"
                        title="Segna come consegnato"
                        aria-label="Consegnato"
                      />
                      <button
                        v-else-if="cellState(item) === 'blocked'"
                        class="secondary-btn procure-btn"
                        @click="openProcureModal(item)"
                        :disabled="procuring"
                        title="Registra l'arrivo della merce ordinata per abilitare la consegna"
                      >
                        ORDINA
                      </button>
                    </template>
                    <!-- Riga Fuori Magazzino: dropdown stato + checkbox (sc-47879) -->
                    <template v-else>
                      <!-- Arrivato: stato definitivo, resta solo la consegna -->
                      <AppSelect
                        v-if="item.outstockState !== 'Arrivato'"
                        class="outstock-state-select"
                        :modelValue="item.outstockState"
                        :options="stateOptions"
                        :disabled="changingOutstockState"
                        @change="onOutstockStateChange(item, $event)"
                        title="Stato approvvigionamento articolo fuori magazzino"
                      />
                      <!-- Nessuna checkbox finché non è arrivato: la consegna
                           non è un'opzione, non una casella disabilitata. -->
                      <input
                        v-if="cellState(item) === 'checkbox'"
                        type="checkbox"
                        v-model="item.isDelivered"
                        :disabled="togglingDelivery[index] || togglingAll"
                        @change="onToggleDelivered(item, index)"
                        title="Segna come consegnato"
                        aria-label="Consegnato"
                      />
                    </template>
                  </template>
                </td>
              </tr>
              <tr v-if="items.length === 0">
                <td :colspan="isConfirmed ? 8 : 7" class="no-result">
                  Nessun articolo presente
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- TOTALI -->
      <section class="detail-section detail-section--totals">
        <h3 class="detail-section__title">Totali</h3>
        <div class="detail-grid">
          <div class="detail-row">
            <span class="detail-label">Totale preventivo</span>
            <span class="detail-value">{{
              formatPriceToPrint(Number(quote?.totalPrice) || 0)
            }}</span>
          </div>
          <div class="detail-row" v-if="discountValue > 0">
            <span class="detail-label">Sconto applicato</span>
            <span class="detail-value"
              >- {{ formatPriceToPrint(discountValue) }}</span
            >
          </div>
          <div class="detail-row" v-if="depositValue > 0">
            <span class="detail-label">Acconto applicato</span>
            <span class="detail-value"
              >- {{ formatPriceToPrint(depositValue) }}</span
            >
          </div>
          <div class="detail-row detail-row--highlight">
            <span class="detail-label">Totale da saldare</span>
            <span class="detail-value">
              <span
                v-if="creditoDaRestituire > 0"
                class="da-restituire"
                title="Il cliente ha pagato più del totale"
              >
                Da restituire {{ formatPriceToPrint(creditoDaRestituire) }}
              </span>
              <template v-else>{{
                formatPriceToPrint(residualToPay)
              }}</template>
            </span>
          </div>
        </div>
      </section>

      <!-- AZIONI -->
      <div class="save-button-container">
        <p v-if="modificaChiusa" class="modifica-chiusa">
          {{ msgPreventivoChiuso }}
        </p>
        <button class="btn-secondary" @click="goBack">Indietro</button>
        <button
          v-if="!modificaChiusa"
          class="btn-primary"
          @click="goToEdit"
          title="Modifica preventivo"
        >
          <i class="fa-solid fa-pen-to-square" style="margin-right: 8px"></i>
          Modifica
        </button>
      </div>
    </template>

    <ProcureModal
      v-model="showProcureModal"
      :title="procureTarget?.name || ''"
      :maxQuantity="procureTarget?.quantityToProcure || 0"
      :unit="procureTarget?.soldUnit || 'pz'"
      :submitting="procuring"
      @confirm="confirmProcure"
    />

    <!-- [sc-49210] Dettaglio articolo: le colonne secondarie -->
    <RowDetailsModal
      v-model="showRowDetails"
      :title="rowDetailsItem?.name || ''"
    >
      <template v-if="rowDetailsItem">
        <div>
          <dt>Categoria</dt>
          <dd>{{ rowDetailsItem.category }}</dd>
        </div>
        <div>
          <dt>U.m.</dt>
          <dd>{{ rowDetailsItem.unit }}</dd>
        </div>
        <div>
          <dt>Venduti</dt>
          <dd>
            <span
              v-if="showProcureBadge(rowDetailsItem)"
              class="venduti venduti--procure"
              :title="procureTitle(rowDetailsItem)"
            >
              {{ rowDetailsItem.sold }}
              <span class="badge-procure"
                >da ordinare {{ rowDetailsItem.quantityToProcure }}</span
              >
            </span>
            <span v-else class="venduti">
              {{ rowDetailsItem.sold }}
              <span
                v-if="showCommittedBadge(rowDetailsItem)"
                class="badge-committed"
                :title="committedTitle(rowDetailsItem)"
              >
                impegnato
              </span>
            </span>
          </dd>
        </div>
        <div>
          <dt>IVA</dt>
          <dd>{{ rowDetailsItem.iva }}%</dd>
        </div>
      </template>
    </RowDetailsModal>
  </main>
</template>

<script>
import axiosInstance from "@/axiosInstance";
import { mapActions } from "vuex/dist/vuex.cjs.js";
import {
  formatPriceToPrint,
  formatToLocalDateAndHours,
} from "../utilities/formatting";
import { itemFullDescription } from "../utilities/itemDescription";
import {
  deliveryCellState,
  outstockStateOptions,
} from "../utilities/procurement";
import {
  daRestituire,
  MSG_PREVENTIVO_CHIUSO,
  preventivoNonModificabile,
} from "../utilities/pagamenti";
import ProcureModal from "../components/ProcureModal.vue";
import RowDetailsModal from "../components/RowDetailsModal.vue";
import { shouldOpenRowDetails } from "../utilities/rowDetails";

export default {
  name: "QuoteDetailPage",

  components: { ProcureModal, RowDetailsModal },

  data() {
    return {
      stateOptions: outstockStateOptions, // [sc-49210] con i colori dei pallini
      showRowDetails: false,
      rowDetailsItem: null,
      quote: null,
      items: [],
      isLoading: false,
      loadError: "",
      togglingDelivery: {},
      togglingAll: false,
      showProcureModal: false,
      procureTarget: null,
      procuring: false,
      changingOutstockState: false,
      msgPreventivoChiuso: MSG_PREVENTIVO_CHIUSO,
    };
  },

  computed: {
    quoteId() {
      return this.$route.params.quoteId;
    },
    isConfirmed() {
      return !!this.quote?.isConfirmed;
    },
    // Preventivo archiviato = chiuso (consegnato o annullato): le consegne non
    // si gestiscono più, la colonna diventa sola lettura (sc-47876).
    isArchived() {
      return !!this.quote?.isArchived;
    },
    // sc-48744: deletedAt valorizzato = eliminato dalle liste
    isEliminato() {
      return !!this.quote?.deletedAt;
    },
    // Solo le righe consegnabili contano per la master: quelle bloccate da
    // ordinare non potranno mai essere spuntate (sc-47876).
    deliverableItems() {
      return this.items.filter((i) => this.cellState(i) !== "blocked");
    },
    // Almeno una riga bloccata da ordinare: la master lo spiega col tooltip.
    hasBlockedItems() {
      return this.items.some((i) => this.cellState(i) === "blocked");
    },
    allDelivered() {
      return (
        this.deliverableItems.length > 0 &&
        this.deliverableItems.every((i) => i.isDelivered)
      );
    },
    someDelivered() {
      return this.deliverableItems.some((i) => i.isDelivered);
    },
    client() {
      // Il GET /quote/:id potrebbe non includere l'associazione Client:
      // in tal caso usiamo clientName come fallback per il nome.
      return this.quote?.Client || { clientName: this.quote?.clientName || "" };
    },
    formattedDate() {
      return this.quote?.date
        ? formatToLocalDateAndHours(this.quote.date)
        : "N/D";
    },
    discountValue() {
      return Number(this.quote?.discount) || 0;
    },
    depositValue() {
      return Number(this.quote?.deposit) || 0;
    },
    // sc-48721: le righe in pagina seguono le spunte di consegna senza
    // ricaricare il preventivo.
    modificaChiusa() {
      return preventivoNonModificabile({
        ...this.quote,
        quote_items: this.items,
      });
    },
    // GET /quote/:id non espone realTotalPrice e residualToPay taglia a zero:
    // il credito si ricava dal rimanente non troncato, con la stessa formula
    // del backend (totale − sconto − acconto).
    creditoDaRestituire() {
      const total = Number(this.quote?.totalPrice) || 0;
      return daRestituire(total - this.discountValue - this.depositValue);
    },
    residualToPay() {
      // realTotalPrice è già calcolato dal backend (totale − sconto − acconto).
      if (this.quote?.realTotalPrice != null) {
        return Number(this.quote.realTotalPrice) || 0;
      }
      const total = Number(this.quote?.totalPrice) || 0;
      return Math.max(0, total - this.discountValue - this.depositValue);
    },
  },

  watch: {
    someDelivered() {
      this.syncMasterIndeterminate();
    },
    allDelivered() {
      this.syncMasterIndeterminate();
    },
  },

  async mounted() {
    await this.fetchQuote();
  },

  methods: {
    onRowClick(event, item) {
      if (!shouldOpenRowDetails(event)) return;
      this.rowDetailsItem = item;
      this.showRowDetails = true;
    },
    ...mapActions([
      "setQuoteItemDelivered",
      "setAllQuoteItemsDelivered",
      "procureQuoteItems",
      "updateOutstockState",
    ]),

    formatPriceToPrint,

    cellState(item) {
      return deliveryCellState({
        isConfirmed: this.isConfirmed,
        isStock: item.isStock,
        isDelivered: item.isDelivered,
        quantityToProcure: item.quantityToProcure,
        outstockState: item.outstockState,
      });
    },

    // Per i fuori magazzino non esiste una quota "da magazzino": tutto è da
    // ordinare al fornitore, quindi il "0 da magazzino" sarebbe fuorviante.
    procureTitle(item) {
      return item.isStock
        ? `${item.quantityWarehouse} da magazzino + ${item.quantityToProcure} da ordinare`
        : `Fuori magazzino: ${item.quantityToProcure} da ordinare al fornitore`;
    },

    // Badge "impegnato": magazzino = unità prelevate dallo stock; outstock =
    // merce ordinata e arrivata, comunque destinata a questo cliente.
    showCommittedBadge(item) {
      if (!(item.sold > 0)) return false;
      return item.isStock || item.outstockState === "Arrivato";
    },

    committedTitle(item) {
      return item.isStock
        ? "Unità prese dal magazzino e impegnate per questo cliente"
        : "Unità arrivate e impegnate per questo cliente";
    },

    // Badge "da ordinare": per gli item di magazzino dipende da needsProcurement,
    // per gli outstock (sc-47879) solo se ancora "Da Ordinare".
    showProcureBadge(item) {
      if (!item.needsProcurement) return false;
      if (item.isStock) return true;
      return item.outstockState === "Da Ordinare";
    },

    openProcureModal(item) {
      this.procureTarget = item;
      this.showProcureModal = true;
    },

    // Registra l'arrivo della merce: il server sposta la quantità da
    // "da ordinare" a "da magazzino". Nessun update ottimistico — i valori
    // autoritativi tornano nella risposta.
    async confirmProcure(quantity) {
      const target = this.procureTarget;
      if (!target?.quoteItemId) return;

      this.procuring = true;
      try {
        const res = await this.procureQuoteItems({
          allocations: [{ quoteItemId: target.quoteItemId, quantity }],
        });

        const updated = res?.data?.items?.[0];
        if (updated) {
          target.quantityToProcure = Number(updated.quantityToProcure);
          target.quantityWarehouse = Number(updated.quantityWarehouse);
          target.needsProcurement = target.quantityToProcure > 0;
        }

        this.showProcureModal = false;
        this.procureTarget = null;
        this.$toast.success(
          target.quantityToProcure > 0
            ? `Ordine registrato. Restano ${target.quantityToProcure} ${target.soldUnit} da ordinare`
            : "Ordine registrato: consegna sbloccata"
        );
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.message ||
            "Impossibile registrare l'ordine dell'articolo"
        );
        console.error("Errore registrazione ordine:", err);
        // Lo stato lato server potrebbe essere cambiato sotto di noi (409):
        // ricarico per ripartire dai valori veri.
        await this.fetchQuote();
        this.showProcureModal = false;
        this.procureTarget = null;
      } finally {
        this.procuring = false;
      }
    },

    // Cambia lo stato del prodotto Outstock dalla riga di preventivo (sc-47879).
    // Lo stato è sul prodotto: la ricarica riflette gli effetti (badge/consegna).
    async onOutstockStateChange(item, newState) {
      if (!item.outstockId || item.outstockState === newState) return;
      const previous = item.outstockState;
      this.changingOutstockState = true;
      try {
        await this.updateOutstockState({
          outstockId: item.outstockId,
          state: newState,
        });
        // Ricarico i valori autoritativi (stato preventivo incluso).
        await this.fetchQuote();
        this.$toast.success(`Stato fuori magazzino aggiornato a "${newState}"`);
      } catch (err) {
        item.outstockState = previous;
        this.$toast.error(
          err?.response?.data?.message ||
            "Impossibile aggiornare lo stato dell'articolo fuori magazzino"
        );
        console.error("Errore cambio stato outstock:", err);
      } finally {
        this.changingOutstockState = false;
      }
    },

    // L'attributo `indeterminate` è solo una proprietà DOM: la sincronizziamo a mano.
    syncMasterIndeterminate() {
      this.$nextTick(() => {
        const el = this.$refs.masterCheckbox;
        if (el) el.indeterminate = this.someDelivered && !this.allDelivered;
      });
    },

    async fetchQuote() {
      this.isLoading = true;
      this.loadError = "";
      try {
        const { data } = await axiosInstance.get(`/quote/${this.quoteId}`);
        const quote = data.quote;
        this.quote = quote;
        this.items = this.normalizeItems(quote);
        this.syncMasterIndeterminate();
      } catch (err) {
        console.error("Errore caricamento dettaglio preventivo:", err);
        this.loadError =
          err?.response?.data?.message ||
          err?.response?.data?.details ||
          "Impossibile caricare il preventivo.";
      } finally {
        this.isLoading = false;
      }
    },

    // Normalizza i quote_items in righe display (stessa logica di buildQuoteTable)
    normalizeItems(quote) {
      const lower = (v) => String(v || "").toLowerCase();
      return (quote?.quote_items || []).map((qi) => {
        const isStock = !!qi.item;
        const src = qi.item || qi.outstock || {};
        const isStockTiles = lower(qi.item?.category) === "piastrelle";
        const isOutTiles = lower(qi.outstock?.subCategory) === "piastrelle";
        const isTiles = isStockTiles || isOutTiles;

        // sc-48197: stessa regola di buildQuoteTable, colore compreso.
        // Niente withMeasure: la misura non faceva parte del titolo nemmeno prima.
        // itemFullDescription aggiunge tipo/posizione ai sanitari (sc-48197).
        const name = qi.item || qi.outstock ? itemFullDescription(src) : "-";
        const category = isStock ? src.category || "-" : "Fuori Magazzino";

        let qty;
        if (isStockTiles) {
          const sqmPerBox = Number(src.sqmPerBox || 0);
          qty = sqmPerBox
            ? Number((sqmPerBox * qi.quantity).toFixed(2))
            : Number(qi.quantity || 0);
        } else {
          qty = Number(qi.quantity || 0);
        }

        const unit = lower(name).includes("battiscopa")
          ? "mL"
          : isTiles
          ? "mQ"
          : "pz";

        // "Venduti" (feature sc-47204): quantità venduta in unità nativa della riga
        // (piastrelle → scatole, sanitari → pezzi) + split magazzino/da ordinare
        // già calcolato dal backend (sc-47203). Le righe outstock hanno tutto da ordinare.
        const sold = Number(qi.quantity || 0);
        const quantityToProcure = Number(qi.quantityToProcure || 0);
        const quantityWarehouse = Number(
          qi.quantityWarehouse ?? sold - quantityToProcure
        );
        const needsProcurement =
          qi.needsProcurement != null
            ? !!qi.needsProcurement
            : quantityToProcure > 0;
        const soldUnit = isTiles ? "scatole" : "pz";

        return {
          quoteItemId: qi.id,
          itemId: qi.itemId ?? qi.item?.id ?? null,
          outstockId: qi.outstockId ?? qi.outstock?.id ?? null,
          outstockState: qi.outstock?.state ?? null,
          isStock,
          name,
          category,
          qty,
          unit,
          price: Number(qi.price || 0),
          iva: Number(qi.iva ?? 22),
          isDelivered: !!qi.isDelivered,
          // Venduti
          sold,
          soldUnit,
          quantityWarehouse,
          quantityToProcure,
          needsProcurement,
        };
      });
    },

    // Toggle stato "consegnato": PATCH immediata, revert su errore (409 = no-op)
    async onToggleDelivered(item, index) {
      if (!item.quoteItemId) return;
      const desired = item.isDelivered;
      this.togglingDelivery[index] = true;
      try {
        const res = await this.setQuoteItemDelivered({
          quoteItemId: item.quoteItemId,
          isDelivered: desired,
        });
        // Lo stato del preventivo è ricalcolato lato server: aggiorno il badge.
        const newState = res?.data?.quoteState;
        if (newState && this.quote) this.quote.state = newState;
        this.$toast.success(
          desired
            ? "Articolo segnato come consegnato"
            : "Articolo non più consegnato"
        );
      } catch (err) {
        if (err?.response?.status !== 409) {
          item.isDelivered = !desired;
          this.$toast.error(
            "Impossibile aggiornare lo stato di consegna dell'articolo"
          );
          console.error("Errore aggiornamento stato consegna:", err);
        }
      } finally {
        delete this.togglingDelivery[index];
      }
    },

    // Flagga/sflagga tutti gli articoli in un colpo: un'unica chiamata bulk
    // (lo stato del preventivo è ricalcolato lato server senza race).
    // Update ottimistico con revert globale in caso di errore.
    async toggleAllDelivered(desired) {
      if (this.togglingAll) return;
      const targets = this.deliverableItems.filter(
        (i) => i.quoteItemId && i.isDelivered !== desired
      );
      if (targets.length === 0) return;

      const snapshot = targets.map((i) => ({ item: i, was: i.isDelivered }));
      this.togglingAll = true;
      targets.forEach((i) => {
        i.isDelivered = desired;
      });

      try {
        const res = await this.setAllQuoteItemsDelivered({
          quoteId: this.quoteId,
          quoteItemIds: targets.map((i) => i.quoteItemId),
          isDelivered: desired,
        });
        const newState = res?.data?.quoteState;
        if (newState && this.quote) this.quote.state = newState;
        const blockedCount = this.items.filter(
          (i) => this.cellState(i) === "blocked"
        ).length;
        this.$toast.success(
          desired
            ? blockedCount > 0
              ? `Articoli consegnabili segnati come consegnati (${blockedCount} ancora da ordinare)`
              : "Tutti gli articoli segnati come consegnati"
            : "Tutti gli articoli segnati come non consegnati"
        );
      } catch (err) {
        snapshot.forEach((s) => {
          s.item.isDelivered = s.was;
        });
        this.$toast.error(
          "Impossibile aggiornare lo stato di consegna degli articoli"
        );
        console.error("Errore aggiornamento stato consegna massivo:", err);
      } finally {
        this.togglingAll = false;
      }
    },

    goToEdit() {
      this.$router.push({
        name: "edit-quote",
        params: { quoteId: this.quoteId },
      });
    },

    goBack() {
      this.$router.push({ name: "quotes" });
    },
  },
};
</script>

<style src="@/css/CreateQuotePage.css"></style>
<style scoped>
#quote-detail-page.page-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 24px;
}

@media (min-width: 1024px) {
  #quote-detail-page.page-container {
    padding: 48px;
  }
}

.detail-loader,
.detail-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
}

.detail-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.detail-title {
  margin: 0;
}

.detail-state {
  padding: 4px 12px;
  border-radius: 999px;
  background: #eee;
  font-size: 0.85rem;
  font-weight: 600;
}

/* sc-48744: archiviato in ambra come gli avvisi, eliminato in rosso */
.detail-state--archived {
  border: 1px solid #ecc97a;
  background: #fff7e6;
  color: #6b4700;
}

.detail-state--deleted {
  border: 1px solid #e0a2a2;
  background: #fdecec;
  color: #8a1f1f;
}

.detail-banner {
  margin: 0 0 16px;
  padding: 8px 12px;
  border: 1px solid #ecc97a;
  border-radius: 8px;
  background: #fff7e6;
  color: #6b4700;
  font-size: 13px;
}

.detail-banner--deleted {
  border-color: #e0a2a2;
  background: #fdecec;
  color: #8a1f1f;
}

.detail-section {
  margin-bottom: 28px;
}

/* Dettaglio cliente + preventivo affiancati su 2 colonne (collassa su mobile) */
.detail-two-cols {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 48px;
  margin-bottom: 28px;
}

.detail-two-cols .detail-section {
  margin-bottom: 0;
}

/* Colonna divisoria verticale tra le due card */
.detail-two-cols .detail-section:first-child {
  border-right: 1px solid #e2e2e2;
  padding-right: 48px;
}

/* Dentro le card affiancate i campi vanno in colonna singola per non stringere */
.detail-two-cols .detail-grid {
  grid-template-columns: minmax(0, 1fr);
}

@media (max-width: 768px) {
  .detail-two-cols {
    grid-template-columns: minmax(0, 1fr);
    gap: 0;
  }

  .detail-two-cols .detail-section {
    margin-bottom: 28px;
  }

  /* Su mobile niente divisore verticale: le card sono impilate */
  .detail-two-cols .detail-section:first-child {
    border-right: none;
    padding-right: 0;
  }
}

.detail-section__title {
  margin: 0 0 12px;
  font-size: 1.05rem;
  border-bottom: 1px solid #e2e2e2;
  padding-bottom: 6px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 24px;
}

.detail-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-row--full {
  grid-column: 1 / -1;
}

.detail-row--highlight .detail-value {
  font-weight: 700;
  font-size: 1.1rem;
}

.detail-label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #888;
}

.detail-value {
  font-size: 0.95rem;
  word-break: break-word;
}

.detail-table-wrap {
  overflow-x: auto;
}

.detail-items-table {
  width: 100%;
  border-collapse: collapse;
}

.detail-items-table th,
.detail-items-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #ececec;
  font-size: 0.92rem;
  vertical-align: middle;
  /* testo a capo: la tabella si adatta allo schermo (sc-49210) */
  white-space: normal;
}

.detail-items-table thead th {
  background: #f5f5f5;
  font-weight: 600;
}

.detail-items-table .detail-item-name {
  font-weight: 600;
  white-space: normal;
}

.col-left {
  text-align: left;
}

.col-right {
  text-align: right;
}

.col-center {
  text-align: center;
}

/* --- Colonna "Venduti" (sc-47204) --- */
.venduti--procure {
  color: #b35309;
  font-weight: 600;
}

.badge-procure {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  background: #fff3e0;
  color: #b35309;
  white-space: nowrap;
}

/* sc-47876: quota presa dal magazzino e impegnata per il cliente del preventivo. */
.badge-committed {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 11px;
  background: #e7f5ec;
  color: #1b7a43;
  white-space: nowrap;
}

.detail-items-table .delivered-cell input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.delivered-cell {
  white-space: nowrap;
}

.procure-btn {
  margin-left: 0.5rem;
  padding: 0.15rem 0.5rem;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
}

.outstock-state-select {
  display: inline-block;
  vertical-align: middle;
  max-width: 130px;
  margin-right: 0.5rem;
}

.delivered-th {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.delivered-th input {
  width: 16px;
  height: 16px;
  cursor: pointer;
}

@media (max-width: 640px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

/* --- Modalità notte: badge stato + intestazioni tabella --- */
</style>
